import {computed, type ComputedRef, defineComponent, ref, watch} from "vue";
import {debounce} from "lodash";
import {SearchDeadLetterFilters} from "@/app/admin/components/aeg-search-deadletter/search-deadletter.ts";
import type {CodeListValueOrder} from "@/app/shared/helpers/code-list-value-order.ts";
import {InputText, Select, RadioButton} from 'primevue';

export default defineComponent({
    components: { RadioButton, InputText, Select },
    props: {
        messageTypes: {
            type: Array<string>,
            default: [],
            required: true
        },
        debounceWait: {
            type: Number,
            default: 100
        }
    },
    emits: ['search'],
    setup(props, {emit}) {
        const searchField = ref<SearchDeadLetterFilters>(SearchDeadLetterFilters.CorrelationId);
        const deadLetterCorrelationId = ref<string>('');
        const deadLetterMessageType = ref<string>('');
        const deadLetterMessageTypes = computed<Array<string>>(() => {
            return ['all', ...props.messageTypes];
        });
        const searchFields: CodeListValueOrder[] = [
            <CodeListValueOrder>{
                isDefaultValue: true,
                key: SearchDeadLetterFilters.CorrelationId,
                label: 'Correlation-id'
            },
            <CodeListValueOrder>{
                key: SearchDeadLetterFilters.MessageType,
                label: 'Message type'
            }
        ];

        const watchFields: ComputedRef<[string | undefined , string | undefined]> = computed(() => {
            return [
                deadLetterCorrelationId.value,
                deadLetterMessageType.value
            ];
        });

        const uniqueFieldId = (id: string) => {
            return id + crypto.randomUUID();
        }

        const messageTypeSelected = computed<boolean>(() => {
            return searchField.value === SearchDeadLetterFilters.MessageType;
        });

        const correlationIdSelected = computed<boolean>(() => {
            return searchField.value === SearchDeadLetterFilters.CorrelationId;
        });

        const debouncedSearch = debounce(() => {
            emit('search', {
                searchType: searchField.value,
                searchValue: correlationIdSelected.value ? deadLetterCorrelationId.value.replace(/-/gi, '') : deadLetterMessageType.value
            });
        }, props.debounceWait);

        const searchFieldChanged = () => {
            debouncedSearch();
        }

        watch(watchFields, searchFieldChanged, { immediate: false, deep: true });

        return {
            searchFields,
            searchFieldChanged,
            uniqueFieldId,
            searchField,
            deadLetterCorrelationId,
            deadLetterMessageType,
            messageTypeSelected,
            correlationIdSelected,
            deadLetterMessageTypes
        }
    }
});
